import { Dispatch } from 'react-redux';
import { Action } from '../../../actions';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';
import { StoreState } from '../../../reducers';
import { receiveResolutions, receiveDriveItems } from '../actions';
import LocalStorageManager from '../../../utils/localStorage/manager';
import { NewItemState } from './reducer';
import UserAgentUtils from '../../../utils/userAgent';
import { downloadThumb } from '../../OfflineManager/actions';
import { DownloadPriority } from '../../../utils/nativeApi';

const MAXIMUM_LAST_VISIT_DAYS = 30;
const MAXIMUM_VISIT_DATE = new Date(new Date().getTime() - (1000 * 3600 * 24 * MAXIMUM_LAST_VISIT_DAYS));

enum NewDriveItemsActionKeys {
	REQUEST_NEW_DRIVE_ITEMS = '@@newDriveItem/REQUEST_NEW_DRIVE_ITEMS',
	RECEIVED_NEW_DRIVE_ITEMS = '@@newDriveItem/RECEIVED_NEW_DRIVE_ITEMS',
	ADD_ITEM = '@@newDriveItem/ADD_ITEM',
	REMOVE_ITEM = '@@newDriveItem/REMOVE_ITEM',
	INIT_NEW_ITEMS_STORE = '@@newDriveItem/INIT_NEW_ITEMS_STORE',
}

interface LocalStorageNewItemInfos {
	id: number;
	additionDate: number;
}

interface RequestNewDriveItemsAction extends Action {
	type: NewDriveItemsActionKeys.REQUEST_NEW_DRIVE_ITEMS;
}

interface RequestNewDriveItemsAction extends Action {
	type: NewDriveItemsActionKeys.REQUEST_NEW_DRIVE_ITEMS;
}

interface ReceivedNewDriveItemsAction extends Action {
	type: NewDriveItemsActionKeys.RECEIVED_NEW_DRIVE_ITEMS;
}

interface AddNewItemAction extends Action {
	type: NewDriveItemsActionKeys.ADD_ITEM;
	id: number;
}

interface RemoveNewItemAction extends Action {
	type: NewDriveItemsActionKeys.REMOVE_ITEM;
	id: number;
}

interface InitNewItemsStoreAction extends Action {
	type: NewDriveItemsActionKeys.INIT_NEW_ITEMS_STORE;
	storedNewItems: LocalStorageNewItemInfos[];
}

type NewDriveItemsAction = RequestNewDriveItemsAction
	| AddNewItemAction
	| RemoveNewItemAction
	| ReceivedNewDriveItemsAction
	| InitNewItemsStoreAction;

const loadNewItemsStore = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const storedNewItems = LocalStorageManager.getNewItemsStore();

	if (storedNewItems) {
		dispatch({
			type: NewDriveItemsActionKeys.INIT_NEW_ITEMS_STORE,
			storedNewItems
		});
	}
};

const addNewItems = (metas: { [k: number]: Api.DriveItem }) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { items } = getState().newDriveItems;

	Object.keys(metas)
		.filter(id => metas[id].type !== Api.DriveItemType.Directory)
		.forEach(id => {
			if (!items[id]) {
				dispatch({
					type: NewDriveItemsActionKeys.ADD_ITEM,
					id
				});

				if (UserAgentUtils.isMobileApp) {
					dispatch(downloadThumb(parseInt(id, 10), DownloadPriority.Normal, false));
				}
			}
		});

	return Promise.resolve();
};

const removeOlderItems = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { items } = getState().newDriveItems;

	Object.keys(items).forEach(id => {
		if (items[id].additionDate < MAXIMUM_VISIT_DATE.getTime()) {
			dispatch({
				type: NewDriveItemsActionKeys.REMOVE_ITEM,
				id
			});
		}
	});

	return Promise.resolve();
};

const updateNewItemsStore = (metas: { [k: number]: Api.DriveItem }) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	return dispatch(removeOlderItems())
		.then(() => UserAgentUtils.isOnline() ? dispatch(addNewItems(metas)) : {})
		.then(() => dispatch(updateNewItemLocalStorage()));
};

const fetchNewDriveItems = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {

	dispatch(requestNewDriveItems());

	return ApiUtils.getNewContents(MAXIMUM_VISIT_DATE)
		.then(contentBlock => {
			dispatch(updateNewItemsStore(contentBlock.metas));

			if (contentBlock.resolutions) {
				dispatch(receiveResolutions(contentBlock.resolutions));
			}

			dispatch(receiveDriveItems(contentBlock.metas));
			return dispatch(receivedNewDriveItems());
		})
		.catch(() => dispatch(receivedNewDriveItems()));
};

const updateNewItemLocalStorage = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { newDriveItems } = getState();
	const { items } = newDriveItems;

	let newItemsToStore: LocalStorageNewItemInfos[] = [];

	Object.keys(items).forEach(key => {
		const id = parseInt(key, 10);
		const item: NewItemState = items[id];

		newItemsToStore.push({
			id,
			additionDate: item.additionDate
		});
	});

	LocalStorageManager.setNewItems(newItemsToStore);
};

const requestNewDriveItems = (): RequestNewDriveItemsAction => ({ type: NewDriveItemsActionKeys.REQUEST_NEW_DRIVE_ITEMS });

const receivedNewDriveItems = (): ReceivedNewDriveItemsAction => ({ type: NewDriveItemsActionKeys.RECEIVED_NEW_DRIVE_ITEMS });

export { NewDriveItemsActionKeys, NewDriveItemsAction, fetchNewDriveItems,
	LocalStorageNewItemInfos, loadNewItemsStore, updateNewItemsStore
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/New/actions.ts